import React, { useState, useEffect } from 'react';
import './InvoiceManagement.css';

const InvoiceManagement = () => {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showStockCardModal, setShowStockCardModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    documentTypeId: '',
    customerId: '',
    documentDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: '',
    terms: '',
    items: []
  });

  const [currentItem, setCurrentItem] = useState({
    productId: '',
    productName: '',
    productCode: '',
    quantity: 1,
    unitPrice: 0,
    unit: 'Adet',
    discount1Rate: 0,
    discount2Rate: 0,
    discount3Rate: 0,
    taxRate: 18,
    description: ''
  });

  // Stock card form state
  const [stockCardForm, setStockCardForm] = useState({
    name: '',
    subName: '',
    productCode: '',
    brand: '',
    category: '',
    subCategory: '',
    unit: 'Adet',
    stock: 0,
    purchasePrice: 0,
    salePrice: 0,
    taxRate: 18,
    barcode: '',
    location: '',
    notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load document types
      const docTypesResponse = await fetch('/api/documenttypes');
      if (docTypesResponse.ok) {
        const docTypes = await docTypesResponse.json();
        setDocumentTypes(docTypes);
      }

      // Load tax rates
      const taxRatesResponse = await fetch('/api/taxrates');
      if (taxRatesResponse.ok) {
        const rates = await taxRatesResponse.json();
        setTaxRates(rates);
      }

      // Load customers
      const customersResponse = await fetch('/api/customers');
      if (customersResponse.ok) {
        const customersData = await customersResponse.json();
        setCustomers(customersData);
      }

      // Load products
      const productsResponse = await fetch('/api/products');
      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        setProducts(productsData);
      }

      // Load documents
      await loadDocuments();
      
    } catch (error) {
      setError('Veriler yüklenirken hata oluştu: ' + error.message);
      // Fallback to mock data
      setDocumentTypes([
        { id: 1, name: 'Fatura' },
        { id: 2, name: 'İrsaliye' },
        { id: 3, name: 'Teklif' }
      ]);
      setTaxRates([
        { id: 1, rate: 0, name: 'KDV Yok' },
        { id: 2, rate: 1, name: 'KDV %1' },
        { id: 3, rate: 8, name: 'KDV %8' },
        { id: 4, rate: 18, name: 'KDV %18' }
      ]);
      setCustomers([
        { id: 1, name: 'ABC Şirketi', taxNumber: '1234567890' },
        { id: 2, name: 'XYZ Ltd.', taxNumber: '0987654321' }
      ]);
      setProducts([
        { id: 1, name: 'Motor Yağı', productCode: 'MY-001', salePrice: 65.00 },
        { id: 2, name: 'Fren Balatası', productCode: 'FB-001', salePrice: 120.00 }
      ]);
      setDocuments([
        {
          id: 1,
          documentNumber: 'FAT-2024-001',
          documentType: 'Fatura',
          customerName: 'ABC Şirketi',
          documentDate: '2024-01-15',
          dueDate: '2024-02-15',
          totalAmount: 1250.00,
          status: 'Ödendi'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await fetch('/api/documents');
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      } else {
        setError('Dökümanlar yüklenirken hata oluştu');
      }
    } catch (error) {
      setError('Dökümanlar yüklenirken hata oluştu: ' + error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateItemTotals = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unitPrice) || 0;
    const discount1Rate = parseFloat(item.discount1Rate) || 0;
    const discount2Rate = parseFloat(item.discount2Rate) || 0;
    const discount3Rate = parseFloat(item.discount3Rate) || 0;
    const taxRate = parseFloat(item.taxRate) || 0;

    let subtotal = quantity * unitPrice;
    
    // Apply discounts
    subtotal = subtotal * (1 - discount1Rate / 100);
    subtotal = subtotal * (1 - discount2Rate / 100);
    subtotal = subtotal * (1 - discount3Rate / 100);
    
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    return {
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const addItem = () => {
    if (!currentItem.productId || currentItem.quantity <= 0) {
      alert('Lütfen ürün seçin ve miktar girin');
      return;
    }

    const selectedProduct = products.find(p => p.id == currentItem.productId);
    if (selectedProduct) {
      const newItem = {
        ...currentItem,
        productName: selectedProduct.name,
        productCode: selectedProduct.productCode,
        unitPrice: selectedProduct.salePrice,
        ...calculateItemTotals(currentItem)
      };

      setFormData(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));

      // Reset current item
      setCurrentItem({
        productId: '',
        productName: '',
        productCode: '',
        quantity: 1,
        unitPrice: 0,
        unit: 'Adet',
        discount1Rate: 0,
        discount2Rate: 0,
        discount3Rate: 0,
        taxRate: 18,
        description: ''
      });
    }
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateDocumentTotals = () => {
    const totals = formData.items.reduce((acc, item) => {
      acc.subtotal += parseFloat(item.subtotal) || 0;
      acc.taxAmount += parseFloat(item.taxAmount) || 0;
      acc.total += parseFloat(item.total) || 0;
      return acc;
    }, { subtotal: 0, taxAmount: 0, total: 0 });

    return {
      subtotal: totals.subtotal.toFixed(2),
      taxAmount: totals.taxAmount.toFixed(2),
      total: totals.total.toFixed(2)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const totals = calculateDocumentTotals();
      const documentData = {
        ...formData,
        ...totals,
        status: 'Beklemede'
      };

      const response = await fetch('/api/documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentData),
      });

      if (response.ok) {
        const newDocument = await response.json();
        setDocuments(prev => [...prev, newDocument]);
        setShowCreateForm(false);
        setFormData({
          documentTypeId: '',
          customerId: '',
          documentDate: new Date().toISOString().split('T')[0],
          dueDate: '',
          notes: '',
          terms: '',
          items: []
        });
      } else {
        setError('Döküman kaydedilirken hata oluştu');
      }
    } catch (error) {
      setError('Döküman kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const handleStockCardSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stockCardForm),
      });

      if (response.ok) {
        const newProduct = await response.json();
        setProducts(prev => [...prev, newProduct]);
        setShowStockCardModal(false);
        setStockCardForm({
          name: '',
          subName: '',
          productCode: '',
          brand: '',
          category: '',
          subCategory: '',
          unit: 'Adet',
          stock: 0,
          purchasePrice: 0,
          salePrice: 0,
          taxRate: 18,
          barcode: '',
          location: '',
          notes: ''
        });
        alert('Stok kartı başarıyla eklendi!');
      } else {
        setError('Stok kartı kaydedilirken hata oluştu');
      }
    } catch (error) {
      setError('Stok kartı kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const getDocumentTypeName = (typeId) => {
    const docType = documentTypes.find(dt => dt.id == typeId);
    return docType ? docType.name : 'Bilinmiyor';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ödendi': return 'green';
      case 'Beklemede': return 'orange';
      case 'Gecikmiş': return 'red';
      default: return 'gray';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.documentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !selectedStatus || doc.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <div className="invoice-management">
      <div className="header">
        <h2>Fatura Yönetimi</h2>
        <div className="header-actions">
          <button className="add-button" onClick={() => setShowCreateForm(true)}>
            + Yeni Fatura
          </button>
          <button className="stock-card-button" onClick={() => setShowStockCardModal(true)}>
            + Stok Kartı Ekle
          </button>
        </div>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Fatura ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">Tüm Durumlar</option>
          <option value="Beklemede">Beklemede</option>
          <option value="Ödendi">Ödendi</option>
          <option value="Gecikmiş">Gecikmiş</option>
        </select>
      </div>

      <div className="documents-table">
        <table>
          <thead>
            <tr>
              <th>Fatura No</th>
              <th>Müşteri</th>
              <th>Tarih</th>
              <th>Vade</th>
              <th>Tutar</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocuments.map(doc => (
              <tr key={doc.id}>
                <td>{doc.documentNumber}</td>
                <td>{doc.customerName}</td>
                <td>{doc.documentDate}</td>
                <td>{doc.dueDate}</td>
                <td>₺{doc.totalAmount}</td>
                <td>
                  <span className={`status ${getStatusColor(doc.status)}`}>
                    {doc.status}
                  </span>
                </td>
                <td>
                  <button className="view-btn">Görüntüle</button>
                  <button className="edit-btn">Düzenle</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>Yeni Fatura Oluştur</h3>
              <button onClick={() => setShowCreateForm(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Döküman Tipi *</label>
                  <select
                    name="documentTypeId"
                    value={formData.documentTypeId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seçin</option>
                    {documentTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Müşteri *</label>
                  <select
                    name="customerId"
                    value={formData.customerId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Müşteri Seçin</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Fatura Tarihi *</label>
                  <input
                    type="date"
                    name="documentDate"
                    value={formData.documentDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Vade Tarihi</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="items-section">
                <h4>Fatura Kalemleri</h4>
                <div className="item-form">
                  <div className="item-form-grid">
                    <div className="form-group">
                      <label>Ürün *</label>
                      <select
                        name="productId"
                        value={currentItem.productId}
                        onChange={handleItemInputChange}
                        required
                      >
                        <option value="">Ürün Seçin</option>
                        {products.map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name} ({product.productCode})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Miktar *</label>
                      <input
                        type="number"
                        name="quantity"
                        value={currentItem.quantity}
                        onChange={handleItemInputChange}
                        min="1"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Birim Fiyat</label>
                      <input
                        type="number"
                        step="0.01"
                        name="unitPrice"
                        value={currentItem.unitPrice}
                        onChange={handleItemInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>İndirim 1 (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="discount1Rate"
                        value={currentItem.discount1Rate}
                        onChange={handleItemInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>İndirim 2 (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="discount2Rate"
                        value={currentItem.discount2Rate}
                        onChange={handleItemInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>İndirim 3 (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="discount3Rate"
                        value={currentItem.discount3Rate}
                        onChange={handleItemInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>KDV (%)</label>
                      <select
                        name="taxRate"
                        value={currentItem.taxRate}
                        onChange={handleItemInputChange}
                      >
                        {taxRates.map(rate => (
                          <option key={rate.id} value={rate.rate}>{rate.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Açıklama</label>
                      <input
                        type="text"
                        name="description"
                        value={currentItem.description}
                        onChange={handleItemInputChange}
                      />
                    </div>
                  </div>
                  <button type="button" onClick={addItem} className="add-item-btn">
                    + Kalem Ekle
                  </button>
                </div>

                <div className="items-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Ürün</th>
                        <th>Miktar</th>
                        <th>Birim Fiyat</th>
                        <th>İndirim</th>
                        <th>KDV</th>
                        <th>Toplam</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.productName}</td>
                          <td>{item.quantity}</td>
                          <td>₺{item.unitPrice}</td>
                          <td>%{item.discount1Rate + item.discount2Rate + item.discount3Rate}</td>
                          <td>%{item.taxRate}</td>
                          <td>₺{item.total}</td>
                          <td>
                            <button type="button" onClick={() => removeItem(index)} className="remove-btn">
                              Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {formData.items.length > 0 && (
                  <div className="totals-section">
                    <div className="totals">
                      <div className="total-row">
                        <span>Ara Toplam:</span>
                        <span>₺{calculateDocumentTotals().subtotal}</span>
                      </div>
                      <div className="total-row">
                        <span>KDV:</span>
                        <span>₺{calculateDocumentTotals().taxAmount}</span>
                      </div>
                      <div className="total-row total">
                        <span>Genel Toplam:</span>
                        <span>₺{calculateDocumentTotals().total}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Notlar</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Şartlar</label>
                <textarea
                  name="terms"
                  value={formData.terms}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
            </form>
            <div className="modal-footer">
              <button onClick={() => setShowCreateForm(false)} className="cancel-btn">
                İptal
              </button>
              <button onClick={handleSubmit} className="save-btn">
                Fatura Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {showStockCardModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Stok Kartı Ekle</h3>
              <button onClick={() => setShowStockCardModal(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleStockCardSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <input
                    type="text"
                    value={stockCardForm.name}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Alt Ad</label>
                  <input
                    type="text"
                    value={stockCardForm.subName}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, subName: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Kodu *</label>
                  <input
                    type="text"
                    value={stockCardForm.productCode}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, productCode: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Marka</label>
                  <input
                    type="text"
                    value={stockCardForm.brand}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, brand: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Kategori</label>
                  <input
                    type="text"
                    value={stockCardForm.category}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, category: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Alt Kategori</label>
                  <input
                    type="text"
                    value={stockCardForm.subCategory}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, subCategory: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Birim</label>
                  <select
                    value={stockCardForm.unit}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, unit: e.target.value }))}
                  >
                    <option value="Adet">Adet</option>
                    <option value="Kg">Kg</option>
                    <option value="Litre">Litre</option>
                    <option value="Metre">Metre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Stok Miktarı</label>
                  <input
                    type="number"
                    value={stockCardForm.stock}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, stock: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Alış Fiyatı</label>
                  <input
                    type="number"
                    step="0.01"
                    value={stockCardForm.purchasePrice}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, purchasePrice: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Satış Fiyatı</label>
                  <input
                    type="number"
                    step="0.01"
                    value={stockCardForm.salePrice}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, salePrice: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>KDV Oranı (%)</label>
                  <input
                    type="number"
                    value={stockCardForm.taxRate}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Barkod</label>
                  <input
                    type="text"
                    value={stockCardForm.barcode}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, barcode: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Depo Konumu</label>
                  <input
                    type="text"
                    value={stockCardForm.location}
                    onChange={(e) => setStockCardForm(prev => ({ ...prev, location: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notlar</label>
                <textarea
                  value={stockCardForm.notes}
                  onChange={(e) => setStockCardForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                />
              </div>
            </form>
            <div className="modal-footer">
              <button onClick={() => setShowStockCardModal(false)} className="cancel-btn">
                İptal
              </button>
              <button onClick={handleStockCardSubmit} className="save-btn">
                Stok Kartı Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement; 